import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { GetRequest, PatchRequest } from "../../../api/Request";
import { SalesAssignee, SalesListFilters, SalesListItem } from "./sales.types";
import { AssignedToFilterEnum, LastActiveFilterEnum, SalesStatusEnum } from "../../../enum";
import { activityRecencyStripeColor, lastActiveOptionList, salesStatusMeta, salesStatusOptionList } from "./salesStatus.config";
import { formatDate, formatRelativeTime, formatTime, getActivityRecencyTier, isRecentlyCreated } from "../../../util/util";
import FormPopupModal from "../../../component/form/formPopupModal/FormPopupModal";
import "./styles/salesList.scss";

const defaultFilters: SalesListFilters = {
  search: "",
  salesStatus: "",
  assignedTo: "",
  lastActive: "",
  page: 1,
  limit: 20,
};

const SalesList: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<SalesListItem[]>([]);
  const [assigneeList, setAssigneeList] = useState<SalesAssignee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<SalesListFilters>(defaultFilters);
  const [commentTarget, setCommentTarget] = useState<{ storeId: string; storeName: string } | null>(null);
  const [commentText, setCommentText] = useState("");
  const [publishingComment, setPublishingComment] = useState(false);

  const fetchSalesList = useCallback(async () => {
    setLoading(true);
    let url = `admin/v1/sales?page=${filters.page}&limit=${filters.limit}`;
    if (filters.search) url += `&search=${encodeURIComponent(filters.search)}`;
    if (filters.salesStatus) url += `&salesStatus=${filters.salesStatus}`;
    if (filters.assignedTo) url += `&assignedTo=${filters.assignedTo}`;
    if (filters.lastActive) url += `&lastActive=${filters.lastActive}`;

    await GetRequest(url)
      .then((res: any) => {
        setData(res.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || "Failed to fetch sales list");
        setLoading(false);
      });
  }, [filters]);

  useEffect(() => {
    GetRequest("admin/v1/sales/assignee").then((res) => setAssigneeList(res.data));
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => fetchSalesList(), 400);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);

  useEffect(() => {
    fetchSalesList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page, filters.limit, filters.salesStatus, filters.assignedTo, filters.lastActive]);

  const handleResetFilters = () => {
    setFilters(defaultFilters);
  };

  const handleStatusChange = async (storeId: string, salesStatus: SalesStatusEnum) => {
    setData((prev) => prev.map((s) => (s._id === storeId ? { ...s, salesStatus } : s)));
    await PatchRequest(`admin/v1/sales/${storeId}/status`, { salesStatus });
  };

  const handleAssignChange = async (storeId: string, assignedTo: string) => {
    const assignee = assigneeList.find((a) => a._id === assignedTo);
    setData((prev) =>
      prev.map((s) =>
        s._id === storeId
          ? { ...s, assignedTo: assignedTo || null, assignedToName: assignee?.name || null }
          : s
      )
    );
    await PatchRequest(`admin/v1/sales/${storeId}/status`, { assignedTo: assignedTo || null });
  };

  const handlePublishComment = async () => {
    if (!commentTarget || !commentText.trim()) return;
    setPublishingComment(true);
    await PatchRequest(`admin/v1/sales/${commentTarget.storeId}/status`, { comment: commentText.trim() });
    setPublishingComment(false);
    setCommentText("");
    setCommentTarget(null);
    fetchSalesList();
  };

  if (loading && data.length === 0) {
    return (
      <div className="salesList">
        <div className="salesListLoading">Loading stores...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="salesList">
        <div className="salesListError">{error}</div>
      </div>
    );
  }

  return (
    <div className="salesList">
      <div className="salesListHeader">
        <h1>Sales</h1>
        <p>Track outreach, status, and assignment for every store</p>
      </div>

      <div className="salesListFilters">
        <div className="filtersRow">
          <div className="filterGroup">
            <label>Search</label>
            <input
              type="text"
              placeholder="Store name or phone number"
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, page: 1, search: e.target.value }))}
            />
          </div>

          <div className="filterGroup">
            <label>Status</label>
            <select
              value={filters.salesStatus}
              onChange={(e) => setFilters((f) => ({ ...f, page: 1, salesStatus: e.target.value as SalesStatusEnum | "" }))}
            >
              <option value="">All</option>
              {salesStatusOptionList.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filterGroup">
            <label>Assigned To</label>
            <select
              value={filters.assignedTo}
              onChange={(e) => setFilters((f) => ({ ...f, page: 1, assignedTo: e.target.value }))}
            >
              <option value="">All</option>
              <option value={AssignedToFilterEnum.Unassigned}>Unassigned</option>
              {assigneeList.map((assignee) => (
                <option key={assignee._id} value={assignee._id}>
                  {assignee.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filterGroup">
            <label>Last Active</label>
            <select
              value={filters.lastActive}
              onChange={(e) => setFilters((f) => ({ ...f, page: 1, lastActive: e.target.value as LastActiveFilterEnum | "" }))}
            >
              <option value="">All</option>
              {lastActiveOptionList.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="filterActions">
          <button className="resetBtn" onClick={handleResetFilters}>
            Reset
          </button>
        </div>
      </div>

      {data.length > 0 ? (
        <>
          <div className="salesListTableContainer">
            <table className="salesListTable">
              <thead>
                <tr>
                  <th>Store / Owner</th>
                  <th>Phone</th>
                  <th>Location</th>
                  <th>Last Active</th>
                  <th>Total Active Days</th>
                  <th>Store Since</th>
                  <th>Status</th>
                  <th>Assigned To</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((store) => {
                  const statusMeta = salesStatusMeta[store.salesStatus];
                  return (
                    <tr key={store._id}>
                      <td>
                        <div className="storeName">
                          {store.name}
                          {isRecentlyCreated(store.createdAt, 3) && <span className="newBadge">New</span>}
                        </div>
                        <div className="ownerName">{store.ownerName || "No owner on record"}</div>
                      </td>
                      <td>{store.phoneNumberList?.[0]?.phoneNumber || "N/A"}</td>
                      <td>{[store.address?.city, store.address?.state].filter(Boolean).join(", ") || "N/A"}</td>
                      <td>
                        <div>
                          <span
                            className="activityDot"
                            style={{
                              background: activityRecencyStripeColor[getActivityRecencyTier(store.activity.lastActiveDate)],
                            }}
                          />
                          {store.activity.lastActiveDate
                            ? formatRelativeTime(store.activity.lastActiveDate)
                            : "Never"}
                        </div>
                        {store.activity.lastActiveDate && (
                          <div className="lastActiveDate">{formatDate(store.activity.lastActiveDate)}</div>
                        )}
                      </td>
                      <td>{store.activity.totalActiveDays}</td>
                      <td>
                        {store.createdAt ? (
                          <>
                            <div>{formatDate(store.createdAt)}</div>
                            <div className="createdTime">{formatTime(store.createdAt)}</div>
                          </>
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td>
                        <select
                          className="salesStatusPill"
                          style={{ background: statusMeta.badgeColor, color: statusMeta.textColor }}
                          value={store.salesStatus}
                          onChange={(e) => handleStatusChange(store._id, e.target.value as SalesStatusEnum)}
                        >
                          {salesStatusOptionList.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          className="assignedSelect"
                          value={store.assignedTo || ""}
                          onChange={(e) => handleAssignChange(store._id, e.target.value)}
                        >
                          <option value="">Unassigned</option>
                          {assigneeList.map((assignee) => (
                            <option key={assignee._id} value={assignee._id}>
                              {assignee.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <div className="salesListActions">
                          <button
                            className="commentBtn"
                            onClick={() => setCommentTarget({ storeId: store._id, storeName: store.name })}
                          >
                            Comment
                          </button>
                          <button className="viewBtn" onClick={() => navigate(`/sales/${store._id}`)}>
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="salesListPagination">
            <div className="paginationInfo">
              Showing {(filters.page - 1) * filters.limit + 1} to{" "}
              {(filters.page - 1) * filters.limit + data.length} stores
            </div>
            <div className="paginationControls">
              <button
                onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}
                disabled={filters.page === 1}
              >
                Previous
              </button>
              <span className="pageNumber">Page {filters.page}</span>
              <button
                onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
                disabled={data.length < filters.limit}
              >
                Next
              </button>
            </div>
          </div>
        </>
      ) : (
        <div className="salesListEmpty">No stores found matching the criteria.</div>
      )}

      {commentTarget && (
        <FormPopupModal closeModal={() => setCommentTarget(null)}>
          <div className="salesCommentPopup">
            <h2>Add Comment</h2>
            <p className="salesCommentStoreName">{commentTarget.storeName}</p>
            <textarea
              placeholder="Write a note about this store..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              autoFocus
            />
            <div className="salesCommentActions">
              <button className="cancelBtn" onClick={() => setCommentTarget(null)}>
                Cancel
              </button>
              <button
                className="publishBtn"
                disabled={!commentText.trim() || publishingComment}
                onClick={handlePublishComment}
              >
                Publish
              </button>
            </div>
          </div>
        </FormPopupModal>
      )}
    </div>
  );
};

export default SalesList;
